import { supabase } from '@/integrations/supabase/client';

/**
 * First-party events for the board and the data desk, written to
 * public.site_events through the track() RPC and read back with site_funnel().
 *
 * Why this exists: Lovable's analytics counted 9,019 visitors for 10 to 24
 * September, 94% "direct", 1,931 of them from Hong Kong, 92% desktop. Mostly
 * bots, and nothing tied an apply, alert or signup to the page a person came
 * from. This records four kinds of event:
 *
 *   view     every route change (pathname only). The landing view also
 *            carries the campaign tags (utm_source, utm_medium, utm_campaign)
 *            and a graphics check, never the rest of the query string
 *   engaged  once per page view, tagged with what triggered it: a mouse wheel
 *            or trackpad, a touch, a click or a key press (input), or 12
 *            seconds visible (time). Only input counts as human: headless
 *            renderers pass the timer, and automation that calls scrollTo
 *            fires scroll events but never wheel or touch ones
 *   act      a named action on a page (search, sort, page, embed_open, the
 *            job-page data strip)
 *   convert  apply, email, alert, signup, carrying the session's attribution:
 *            landing page, referrer host, data pages seen, last data page
 *
 * Conversions are captured at the three choke points every form already goes
 * through (the create-job-alert and capture-job-applicant functions, and
 * auth.signUp), so a new alert form is counted without an edit here.
 *
 * Privacy: a random per-tab id in sessionStorage, the path, and no personal
 * data. Nothing is sent when the browser asks not to be tracked (Do Not Track
 * or Global Privacy Control). Every call is fire-and-forget: a failure here
 * never touches the page.
 */

type Kind = 'view' | 'engaged' | 'act' | 'convert';
type Props = Record<string, string | number | boolean | null | undefined | string[]>;
interface Ev { k: Kind; n?: string; p: string; x?: Props }

const SID = 'af_sid';
const SESS = 'af_sess';
let queue: Ev[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let installed = false;
let engagedFor: string | null = null;
let engageCleanup: (() => void) | null = null;

function optedOut(): boolean {
  if (typeof window === 'undefined') return true;
  const n = navigator as Navigator & { globalPrivacyControl?: boolean };
  return n.doNotTrack === '1' || n.globalPrivacyControl === true;
}

function store(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

function sid(): string {
  const s = store();
  let id = s?.getItem(SID) ?? '';
  if (!/^[A-Za-z0-9_-]{8,40}$/.test(id)) {
    const a = new Uint8Array(12);
    (window.crypto ?? { getRandomValues: (x: Uint8Array) => x.map(() => Math.floor(Math.random() * 256)) }).getRandomValues(a);
    id = Array.from(a, (b) => (b % 36).toString(36)).join('') + Date.now().toString(36);
    try { s?.setItem(SID, id); } catch { /* private mode */ }
  }
  return id;
}

interface Sess {
  land?: string; ref?: string; dataViews: number; lastData?: string; views: number;
  src?: string; med?: string; cmp?: string;
}

/** The query string the app was opened with, read once when this module
 *  loads. Routes can rewrite the URL before the first view is recorded:
 *  /jobs/<uuid> redirects to /jobs/<slug> and drops the query string. */
const ENTRY_SEARCH = typeof window === 'undefined' ? '' : window.location.search;

/** Campaign tags from the landing URL, cleaned to a safe alphabet. ChatGPT
 *  adds utm_source=chatgpt.com to its links, and its app often sends no
 *  referrer, so this is the only way some of that traffic is visible. */
function campaign(): { src?: string; med?: string; cmp?: string } {
  try {
    const q = new URLSearchParams(ENTRY_SEARCH);
    const clean = (v: string | null) => {
      const t = (v ?? '').toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
      return t || undefined;
    };
    return { src: clean(q.get('utm_source')), med: clean(q.get('utm_medium')), cmp: clean(q.get('utm_campaign')) };
  } catch { return {}; }
}

/**
 * How this browser draws WebGL: 'hw' on a real graphics chip, 'soft' when
 * it falls back to a software renderer (SwiftShader, llvmpipe), 'none' when
 * WebGL is unavailable. Headless browsers on cloud machines draw in
 * software: in the first day of measuring, those sessions applied 0% of the
 * time, against about 24% for 'hw'. 'none' is NOT a bot sign (privacy
 * browsers, old devices; they applied). Some scrapers run on GPU machines and
 * report 'hw'; track() catches those by cloud network instead. Runs once per
 * session, on the landing view.
 */
function graphics(): 'hw' | 'soft' | 'none' | undefined {
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl') || c.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return 'none';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const r = String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? '');
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return /swiftshader|llvmpipe|softpipe|software|offscreen/i.test(r) ? 'soft' : 'hw';
  } catch { return undefined; }
}
function sess(): Sess {
  try { return { views: 0, dataViews: 0, ...JSON.parse(store()?.getItem(SESS) ?? '{}') }; } catch { return { views: 0, dataViews: 0 }; }
}
function saveSess(s: Sess) { try { store()?.setItem(SESS, JSON.stringify(s)); } catch { /* ignore */ } }

function send(events: Ev[], keepalive: boolean) {
  const body = { sid: sid(), wd: (navigator as Navigator & { webdriver?: boolean }).webdriver === true, events };
  if (keepalive) {
    // The page is going away; supabase-js cannot ask for keepalive, so post
    // directly. Best effort: if the browser refuses, the events are lost.
    const c = supabase as unknown as { supabaseUrl?: string; supabaseKey?: string };
    if (c.supabaseUrl && c.supabaseKey) {
      try {
        void fetch(`${c.supabaseUrl}/rest/v1/rpc/track`, {
          method: 'POST', keepalive: true,
          headers: { apikey: c.supabaseKey, Authorization: `Bearer ${c.supabaseKey}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ p: body }),
        }).catch(() => undefined);
        return;
      } catch { /* fall through */ }
    }
  }
  void (supabase.rpc as unknown as (fn: string, args: object) => Promise<unknown>)('track', { p: body }).then(() => undefined, () => undefined);
}

function flush(keepalive = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  while (queue.length) send(queue.splice(0, 30), keepalive);
}

function push(e: Ev, now = false) {
  if (optedOut()) return;
  queue.push(e);
  if (now || queue.length >= 20) flush();
  else if (!timer) timer = setTimeout(() => flush(), 2500);
}

function here(): string { return typeof window === 'undefined' ? '/' : window.location.pathname || '/'; }

function watchEngagement(path: string) {
  engageCleanup?.();
  engagedFor = null;
  // 'time' is still recorded so dwell can be read, but only input makes a
  // session human in site_funnel(). Timer first, input later: both are sent.
  // No 'scroll' trigger: in the first day of #403, every session drawing
  // graphics in software "engaged" by scroll alone, and none of them applied.
  let timed = false;
  const fire = (via: 'wheel' | 'touch' | 'pointer' | 'key' | 'time') => {
    if (via === 'time') {
      if (timed || engagedFor === path) return;
      timed = true;
      push({ k: 'engaged', p: path, x: { via } });
      return;
    }
    if (engagedFor === path) return;
    engagedFor = path;
    push({ k: 'engaged', p: path, x: { via } });
    engageCleanup?.();
  };
  const onWheel = () => fire('wheel');
  const onTouch = () => fire('touch');
  const onPointer = () => fire('pointer');
  const onKey = () => fire('key');
  const t = setTimeout(() => { if (document.visibilityState === 'visible') fire('time'); }, 12000);
  window.addEventListener('wheel', onWheel, { passive: true, once: true });
  window.addEventListener('touchstart', onTouch, { passive: true, once: true });
  window.addEventListener('pointerdown', onPointer, { once: true });
  window.addEventListener('keydown', onKey, { once: true });
  engageCleanup = () => {
    clearTimeout(t);
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('touchstart', onTouch);
    window.removeEventListener('pointerdown', onPointer);
    window.removeEventListener('keydown', onKey);
    engageCleanup = null;
  };
}

function attribution(): Props {
  const s = sess();
  return { land: s.land, ref: s.ref, src: s.src, cmp: s.cmp, data_views: s.dataViews, last_data: s.lastData, views: s.views };
}

export const siteEvents = {
  /**
   * Which side of a split test this tab is on: 0 or 1, the same answer for the
   * whole visit. It is a hash of the tab's random id and the test's name, so
   * two tests split independently. A browser that asked not to be tracked gets
   * no id and is always 0, the side that ships if the test wins.
   */
  arm(test: string): 0 | 1 {
    if (optedOut()) return 0;
    let h = 2166136261;
    for (const ch of `${test}:${sid()}`) {
      h ^= ch.charCodeAt(0);
      h = Math.imul(h, 16777619);
    }
    // The low bit of FNV-1a follows the last character too closely; fold the
    // high half in before taking it.
    return (((h >>> 16) ^ h) & 1) as 0 | 1;
  },
  /** This tab's session id, for server-side events that should join the
   *  visit (the /go outbound redirect). Undefined when the browser opted out. */
  sessionId(): string | undefined {
    return optedOut() ? undefined : sid();
  },
  /** A route change. Called by <SiteEventsRoute/>; nothing else needs to. */
  view(path: string) {
    if (optedOut()) return;
    // /jobs/<uuid> redirects to /jobs/<slug> as soon as the job loads; logging
    // both would count every job visit twice. The slug view is the one kept.
    if (/^\/jobs\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i.test(path)) return;
    const s = sess();
    let landing: Props | undefined;
    if (!s.land) {
      s.land = path;
      try {
        const r = document.referrer ? new URL(document.referrer).host : '';
        s.ref = r && r !== window.location.host ? r : undefined;
      } catch { /* ignore */ }
      Object.assign(s, campaign());
      landing = { ref: s.ref, utm_source: s.src, utm_medium: s.med, utm_campaign: s.cmp, gl: graphics() };
    }
    s.views += 1;
    if (path.startsWith('/data')) { s.dataViews += 1; s.lastData = path; }
    saveSess(s);
    push({ k: 'view', p: path, x: landing });
    watchEngagement(path);
  },
  /** A named action on the current page. */
  act(name: string, props?: Props) {
    push({ k: 'act', n: name, p: here(), x: props });
  },
  /** An apply, email capture, alert or signup, with the session's attribution. */
  convert(kind: 'apply' | 'email' | 'alert' | 'signup', props?: Props) {
    push({ k: 'convert', n: kind, p: here(), x: { ...attribution(), ...props } }, true);
  },
};

/**
 * Wire the choke points once. supabase.functions is a getter that builds a new
 * FunctionsClient on every access, so the wrap goes on the prototype, not the
 * instance; auth is a single instance.
 */
export function installSiteEvents() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const FN_KIND: Record<string, 'alert' | 'email'> = {
    'create-job-alert': 'alert',
    'capture-job-applicant': 'email',
  };
  try {
    const proto = Object.getPrototypeOf(supabase.functions) as { invoke: (...a: unknown[]) => Promise<{ error?: unknown }> };
    const original = proto.invoke;
    proto.invoke = async function (this: unknown, ...args: unknown[]) {
      const res = await original.apply(this, args);
      const kind = typeof args[0] === 'string' ? FN_KIND[args[0]] : undefined;
      if (kind && !res?.error) siteEvents.convert(kind);
      return res;
    };
  } catch { /* leave invoke untouched */ }

  try {
    const auth = supabase.auth as unknown as { signUp: (...a: unknown[]) => Promise<{ error?: unknown }> };
    const signUp = auth.signUp.bind(supabase.auth);
    auth.signUp = async (...args: unknown[]) => {
      const res = await signUp(...args);
      if (!res?.error) siteEvents.convert('signup');
      return res;
    };
  } catch { /* leave signUp untouched */ }

  const onHide = () => { if (document.visibilityState === 'hidden') flush(true); };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', () => flush(true));
}
